import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle } from 'lucide-react';
import { t } from '../../lib/i18n';
import { ProfileSelect } from './ProfileSelect';
import {
  GOALS_OPTIONS, FITNESS_LEVELS, MORPHOLOGIES, FACE_SHAPES, SKIN_TYPES, SKIN_TONES, BUDGETS, STYLE_OPTIONS, groomingOptions,
} from './constants';

const inputClass = 'bg-black/20 border-white/10 text-white';

const NumberField = ({ label, value, onChange, placeholder, testId }) => (
  <div className="space-y-2">
    <Label className="text-white/60 text-xs">{label}</Label>
    <Input data-testid={testId} type="number" value={value} onChange={e => onChange(e.target.value)} className={inputClass} placeholder={placeholder} />
  </div>
);

export const StepIdentity = ({ profile, update, language }) => (
  <div className="space-y-4" data-testid="onboarding-step-identity">
    <div className="grid grid-cols-3 gap-3">
      <NumberField testId="onboarding-age" label={t('age', language)} value={profile.age} onChange={v => update('age', v)} placeholder="25" />
      <NumberField testId="onboarding-height" label={t('height', language)} value={profile.height} onChange={v => update('height', v)} placeholder="178" />
      <NumberField testId="onboarding-weight" label={t('weight', language)} value={profile.weight} onChange={v => update('weight', v)} placeholder="75" />
    </div>
    <ProfileSelect
      testId="onboarding-fitness"
      label={t('fitnessLevel', language)}
      placeholder={t('fitnessLevel', language)}
      value={profile.fitness_level}
      onChange={v => update('fitness_level', v)}
      options={FITNESS_LEVELS}
      language={language}
    />
  </div>
);

export const StepMorphology = ({ profile, update, language }) => (
  <div className="space-y-4" data-testid="onboarding-step-morphology">
    <ProfileSelect testId="onboarding-morphology" label={t('morphology', language)} value={profile.morphology} onChange={v => update('morphology', v)} options={MORPHOLOGIES} language={language} />
    <ProfileSelect testId="onboarding-face-shape" label={t('faceShape', language)} value={profile.face_shape} onChange={v => update('face_shape', v)} options={FACE_SHAPES} language={language} />
    <div className="grid grid-cols-2 gap-3">
      <ProfileSelect testId="onboarding-skin-type" label={t('skinType', language)} value={profile.skin_type} onChange={v => update('skin_type', v)} options={SKIN_TYPES} language={language} />
      <ProfileSelect testId="onboarding-skin-tone" label={t('skinTone', language)} value={profile.skin_tone} onChange={v => update('skin_tone', v)} options={SKIN_TONES} language={language} />
    </div>
  </div>
);

const GoalToggle = ({ goal, selected, onToggle, language }) => (
  <button
    type="button"
    onClick={() => onToggle(goal)}
    data-testid={`goal-${goal}`}
    aria-pressed={selected}
    className={`flex items-center gap-2 p-3 rounded-lg border text-sm transition-colors duration-200 ${
      selected
        ? 'bg-lux-gold/10 border-lux-gold/30 text-[var(--lux-amber)]'
        : 'bg-black/20 border-white/10 text-white/60 hover:border-white/20'
    }`}
  >
    <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-[var(--lux-gold)] border-[var(--lux-gold)]' : 'border-white/20'}`}>
      {selected && <CheckCircle className="w-3 h-3 text-[var(--lux-ink)]" />}
    </div>
    {t(goal, language)}
  </button>
);

export const StepGoals = ({ profile, update, toggleGoal, language }) => (
  <div className="space-y-4" data-testid="onboarding-step-goals">
    <Label className="text-white/60 text-xs">{t('goals', language)}</Label>
    <div className="grid grid-cols-2 gap-2">
      {GOALS_OPTIONS.map(goal => (
        <GoalToggle key={goal} goal={goal} selected={profile.goals.includes(goal)} onToggle={toggleGoal} language={language} />
      ))}
    </div>
    <ProfileSelect testId="onboarding-budget" label={t('budget', language)} value={profile.budget} onChange={v => update('budget', v)} options={BUDGETS} language={language} />
    <div className="space-y-2">
      <Label className="text-white/60 text-xs">{t('constraints', language)}</Label>
      <Input
        data-testid="onboarding-constraints"
        value={profile.constraints}
        onChange={e => update('constraints', e.target.value)}
        className={inputClass}
        placeholder={language === 'fr' ? 'Ex: environnement corporate...' : 'Ex: corporate environment...'}
      />
    </div>
  </div>
);

export const StepStyle = ({ profile, update, language }) => (
  <div className="space-y-4" data-testid="onboarding-step-style">
    <ProfileSelect testId="onboarding-current-style" label={t('currentStyle', language)} value={profile.current_style} onChange={v => update('current_style', v)} options={STYLE_OPTIONS} language={language} />
    <ProfileSelect testId="onboarding-grooming" label={t('groomingHabits', language)} value={profile.grooming_habits} onChange={v => update('grooming_habits', v)} options={groomingOptions(language)} language={language} />
  </div>
);

const SUMMARY_FIELDS = [
  { key: 'age', label: 'age' },
  { key: 'height', label: 'height', suffix: 'cm' },
  { key: 'weight', label: 'weight', suffix: 'kg' },
  { key: 'morphology', label: 'morphology', translate: true },
  { key: 'face_shape', label: 'faceShape', translate: true },
  { key: 'skin_type', label: 'skinType', translate: true },
  { key: 'budget', label: 'budget', translate: true },
  { key: 'fitness_level', label: 'fitnessLevel', translate: true },
];

export const StepSummary = ({ profile, language }) => (
  <div className="space-y-4" data-testid="onboarding-step-summary">
    <h3 className="text-lg font-display text-white mb-4">{language === 'fr' ? 'R\u00e9capitulatif' : 'Summary'}</h3>
    <div className="grid grid-cols-2 gap-3 text-sm">
      {SUMMARY_FIELDS.filter(f => profile[f.key]).map(f => (
        <div key={f.key} className="bg-black/20 rounded-lg p-3 border border-white/5">
          <span className="text-white/40 text-xs">{t(f.label, language)}</span>
          <p className="text-white">{f.translate ? t(profile[f.key], language) : `${profile[f.key]}${f.suffix || ''}`}</p>
        </div>
      ))}
    </div>
    {profile.goals.length > 0 && (
      <div className="bg-black/20 rounded-lg p-3 border border-white/5">
        <span className="text-white/40 text-xs">{t('goals', language)}</span>
        <div className="flex flex-wrap gap-2 mt-1">
          {profile.goals.map(g => (
            <span key={g} className="text-[10px] uppercase tracking-wider text-[var(--lux-amber)] bg-lux-gold/10 border border-lux-gold/20 px-2 py-0.5 rounded-full">{t(g, language)}</span>
          ))}
        </div>
      </div>
    )}
  </div>
);
